/**
 * <s-dot-word data-word="THE4" data-accent-last>: a giant word drawn as a field of dots.
 * - Assembles once: the first time it scrolls into view, the dots rise like embers and
 *   spring into place letter by letter.
 * - Mouse: nearby dots swell, warm to coral and lean away on springs (elastic wobble);
 *   a faster mouse reaches further. A click bursts the dots outward with a ripple.
 * - The last character (the "4") stays coral, like the logo.
 * - Idle: a soft warm light sweeps across every few seconds.
 * - The footer talks to it: hovering a footer link sends a ripple up from under that
 *   column ([data-word-ping]); hovering the CTA ([data-word-warm]) warms the whole word.
 * Animates only in view and while something moves. Reduced motion: still dots.
 */
class SDotWord extends HTMLElement {
  #canvas = null;
  #ctx = null;
  #dots = [];
  #ripples = [];
  #mouse = { x: -9999, y: -9999, on: false, speed: 0, t: 0 };
  #frame = 0;
  #visible = false;
  #still = false;
  #assembled = false;
  #dpr = 1;
  #w = 0;
  #h = 0;
  #gap = 8;
  #warm = 0;
  #warmTarget = 0;
  #sweep = null;
  #sweepTimer = 0;
  #beatTimer = 0;
  #beatFrom = Infinity;
  #beatBox = null;
  #family = 'sans-serif';
  #baseSize = 100;
  #order = [];
  #morphCache = new Map();
  #morphWord = null;
  #morphTimer = 0;
  #colors = { base: [255, 255, 255], hot: [255, 132, 120], deep: [232, 88, 75], light: [255, 165, 155] };
  #resizeObserver = null;
  #io = null;
  #scope = null;

  connectedCallback() {
    this.#canvas = this.querySelector('canvas');
    if (!this.#canvas) return;
    this.#ctx = this.#canvas.getContext('2d');
    this.#still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#readColors();

    this.#resizeObserver = new ResizeObserver(() => this.#build());
    this.#resizeObserver.observe(this);

    this.#io = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      this.#visible = entry.isIntersecting;
      if (this.#visible && !this.#assembled && entry.intersectionRatio >= 0.3) this.#assemble();
      if (this.#visible) {
        this.#wake();
        this.#scheduleSweep();
      } else {
        clearTimeout(this.#sweepTimer);
        clearTimeout(this.#beatTimer);
      }
    }, { threshold: [0, 0.3] });
    this.#io.observe(this);

    if (document.fonts?.ready) document.fonts.ready.then(() => this.#build());
    if (this.#still) return;

    this.addEventListener('pointermove', this.#onMove, { passive: true });
    this.addEventListener('pointerleave', this.#onLeave);
    this.addEventListener('pointerdown', this.#onDown);
    this.#scope = this.closest('footer') || document;
    this.#scope.addEventListener('pointerover', this.#onScopeOver);
    this.#scope.addEventListener('pointerout', this.#onScopeOut);
    this.#scope.addEventListener('focusin', this.#onScopeOver);
    this.#scope.addEventListener('focusout', this.#onScopeOut);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#io?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#beatTimer);
    clearTimeout(this.#morphTimer);
    clearTimeout(this.#sweepTimer);
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerleave', this.#onLeave);
    this.removeEventListener('pointerdown', this.#onDown);
    this.#scope?.removeEventListener('pointerover', this.#onScopeOver);
    this.#scope?.removeEventListener('pointerout', this.#onScopeOut);
    this.#scope?.removeEventListener('focusin', this.#onScopeOver);
    this.#scope?.removeEventListener('focusout', this.#onScopeOut);
  }

  #readColors() {
    const style = getComputedStyle(this);
    const parse = (value, fallback) => {
      const hex = value.trim().replace('#', '');
      if (!/^[0-9a-f]{6}$/i.test(hex)) return fallback;
      return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
    };
    this.#colors.base = parse(style.getPropertyValue('--color-on-ink'), this.#colors.base);
    this.#colors.light = parse(style.getPropertyValue('--color-brand-from'), this.#colors.light);
    this.#colors.hot = parse(style.getPropertyValue('--color-brand-via'), this.#colors.hot);
    this.#colors.deep = parse(style.getPropertyValue('--color-brand-to'), this.#colors.deep);
  }

  #build() {
    const rect = this.getBoundingClientRect();
    if (!rect.width) return;
    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.#w = rect.width;
    this.#gap = Math.max(5, Math.round(this.#w / 150));

    const word = (this.dataset.word || 'THE4').trim() || 'THE4';
    const family = getComputedStyle(this).fontFamily || 'sans-serif';
    const probe = document.createElement('canvas').getContext('2d');
    probe.font = `400 100px ${family}`;
    const metrics = probe.measureText(word);
    const ratio = (this.#w / metrics.width) * 0.98;
    const size = 100 * ratio;
    const ascent = (metrics.actualBoundingBoxAscent || 72) * ratio;
    const descent = (metrics.actualBoundingBoxDescent || 0) * ratio;
    this.#h = Math.ceil(ascent + descent + this.#gap * 2);
    const startX = this.#w / 2 - (metrics.width * ratio) / 2;
    const accentFrom = this.hasAttribute('data-accent-last') && word.length > 1
      ? startX + probe.measureText(word.slice(0, -1)).width * ratio
      : Infinity;

    this.style.height = `${this.#h}px`;
    this.#canvas.width = Math.round(this.#w * this.#dpr);
    this.#canvas.height = Math.round(this.#h * this.#dpr);
    this.#canvas.style.width = `${this.#w}px`;
    this.#canvas.style.height = `${this.#h}px`;

    const off = document.createElement('canvas');
    off.width = Math.ceil(this.#w);
    off.height = this.#h;
    const octx = off.getContext('2d');
    octx.fillStyle = '#000';
    octx.font = `400 ${size}px ${family}`;
    octx.textBaseline = 'alphabetic';
    octx.textAlign = 'center';
    octx.fillText(word, this.#w / 2, this.#gap + ascent);
    const data = octx.getImageData(0, 0, off.width, off.height).data;

    this.#family = family;
    this.#baseSize = size;
    this.#morphCache = new Map();
    this.#morphWord = null;
    const pending = !this.#assembled && !this.#still;
    this.#dots = [];
    this.#beatBox = null;
    for (let y = this.#gap / 2; y < this.#h; y += this.#gap) {
      for (let x = this.#gap / 2; x < this.#w; x += this.#gap) {
        const alpha = data[(Math.floor(y) * off.width + Math.floor(x)) * 4 + 3];
        if (alpha <= 128) continue;
        const dot = { x, y, px: 0, py: 0, vx: 0, vy: 0, heat: 0, seed: Math.random(), accent: x >= accentFrom, release: 0, gx: 0, gy: 0, nx: 0, ny: 0, mAt: 0 };
        if (pending) {
          // Start as embers scattered below and around the word.
          dot.px = (Math.random() - 0.5) * this.#w * 0.25;
          dot.py = this.#h * (0.6 + Math.random() * 0.9);
          dot.release = Infinity;
        }
        this.#dots.push(dot);
        if (dot.accent) {
          const box = this.#beatBox || (this.#beatBox = { x0: x, x1: x, y0: y, y1: y });
          box.x0 = Math.min(box.x0, x); box.x1 = Math.max(box.x1, x); box.y0 = Math.min(box.y0, y); box.y1 = Math.max(box.y1, y);
        }
      }
    }
    this.#order = [...this.#dots].sort((a, b) => a.x - b.x || a.y - b.y);
    if (this.#assembled || this.#still) this.#draw(performance.now());
    else this.#ctx.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
    this.#wake();
  }


  /** Points of the dot grid that fall inside `word`, fitted and centred in the canvas. */
  #sample(word) {
    if (this.#morphCache.has(word)) return this.#morphCache.get(word);
    const probe = document.createElement('canvas').getContext('2d');
    probe.font = `400 100px ${this.#family}`;
    const metrics = probe.measureText(word);
    const ratio = Math.min((this.#w * 0.94) / metrics.width, this.#baseSize / 100);
    const ascent = (metrics.actualBoundingBoxAscent || 72) * ratio;
    const descent = (metrics.actualBoundingBoxDescent || 0) * ratio;
    const off = document.createElement('canvas');
    off.width = Math.ceil(this.#w);
    off.height = this.#h;
    const octx = off.getContext('2d');
    octx.fillStyle = '#000';
    octx.font = `400 ${100 * ratio}px ${this.#family}`;
    octx.textBaseline = 'alphabetic';
    octx.textAlign = 'center';
    octx.fillText(word, this.#w / 2, (this.#h - ascent - descent) / 2 + ascent);
    const data = octx.getImageData(0, 0, off.width, off.height).data;
    const points = [];
    for (let x = this.#gap / 2; x < this.#w; x += this.#gap) {
      for (let y = this.#gap / 2; y < this.#h; y += this.#gap) {
        if (data[(Math.floor(y) * off.width + Math.floor(x)) * 4 + 3] > 128) points.push({ x, y });
      }
    }
    this.#morphCache.set(word, points);
    return points;
  }

  /** Fly the dots into another word (a theme name), or back home with `null`. */
  #morphTo(word) {
    if (this.#still || !this.#assembled || !this.#dots.length) return;
    if (word === this.#morphWord) return;
    this.#morphWord = word;
    const now = performance.now();
    const points = word ? this.#sample(word) : null;
    const count = this.#order.length;
    this.#order.forEach((dot, i) => {
      if (points && points.length) {
        const target = points[Math.floor((i * points.length) / count)];
        dot.nx = target.x - dot.x;
        dot.ny = target.y - dot.y;
      } else {
        dot.nx = 0;
        dot.ny = 0;
      }
      // A quick left-to-right wave so the letters re-form in reading order.
      dot.mAt = now + ((dot.x + dot.nx) / this.#w) * 220 + dot.seed * 110;
    });
    this.#wake();
  }

  #assemble() {
    this.#assembled = true;
    this.#beatFrom = performance.now() + 1700;
    const now = performance.now();
    for (const dot of this.#dots) {
      // Letter by letter, left to right, with a little scatter.
      dot.release = now + (dot.x / this.#w) * 900 + dot.seed * 350;
    }
    this.#wake();
  }

  #scheduleSweep() {
    clearTimeout(this.#sweepTimer);
    if (this.#still) return;
    this.#sweepTimer = setTimeout(() => {
      if (!this.#visible) return;
      if (!this.#mouse.on && this.#assembled) {
        this.#sweep = { t: performance.now() };
        this.#wake();
      }
      this.#scheduleSweep();
    }, 6500);
  }

  #onMove = (event) => {
    const rect = this.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const now = performance.now();
    if (this.#mouse.on) {
      const dt = Math.max(now - this.#mouse.t, 8);
      const speed = Math.hypot(x - this.#mouse.x, y - this.#mouse.y) / dt;
      this.#mouse.speed = this.#mouse.speed * 0.7 + speed * 0.3;
    }
    Object.assign(this.#mouse, { x, y, on: true, t: now });
    this.#wake();
  };

  #onLeave = () => {
    this.#mouse.on = false;
    this.#mouse.speed = 0;
    this.#wake();
  };

  #onDown = (event) => {
    const rect = this.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    this.#ripples.push({ x, y, t: performance.now(), strength: 1 });
    const reach = this.#gap * 18;
    for (const dot of this.#dots) {
      const dx = dot.x - x;
      const dy = dot.y - y;
      const dist = Math.hypot(dx, dy);
      if (dist > reach || dist === 0) continue;
      const k = 1 - dist / reach;
      dot.vx += (dx / dist) * k * this.#gap * 3.2;
      dot.vy += (dy / dist) * k * this.#gap * 3.2;
      dot.heat = Math.max(dot.heat, k);
    }
    this.#wake();
  };

  #onScopeOver = (event) => {
    const morphLink = event.target.closest?.('[data-word-morph]');
    if (morphLink) {
      clearTimeout(this.#morphTimer);
      this.#morphTo(morphLink.dataset.wordMorph.toUpperCase());
      return;
    }
    if (event.target.closest?.('[data-word-warm]')) {
      this.#warmTarget = 1;
      this.#wake();
      return;
    }
    const link = event.target.closest?.('[data-word-ping] a');
    if (!link || link === this.#lastPing) return;
    this.#lastPing = link;
    const rect = this.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    const x = Math.min(Math.max(linkRect.left + linkRect.width / 2 - rect.left, 0), this.#w);
    this.#ripples.push({ x, y: -this.#gap * 4, t: performance.now(), strength: 0.55 });
    this.#wake();
  };

  #lastPing = null;

  #onScopeOut = (event) => {
    if (event.target.closest?.('[data-word-morph]') && !event.relatedTarget?.closest?.('[data-word-morph]')) {
      clearTimeout(this.#morphTimer);
      this.#morphTimer = setTimeout(() => this.#morphTo(null), 280);
    }
    if (event.target.closest?.('[data-word-warm]') && !event.relatedTarget?.closest?.('[data-word-warm]')) {
      this.#warmTarget = 0;
      this.#wake();
    }
    if (event.target.closest?.('[data-word-ping] a') && !event.relatedTarget?.closest?.('[data-word-ping] a')) {
      this.#lastPing = null;
    }
  };

  #wake() {
    if (this.#still || !this.#visible || this.#frame) return;
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #tick = (now) => {
    this.#frame = 0;
    const busy = this.#draw(now);
    if (busy && this.#visible) this.#frame = requestAnimationFrame(this.#tick);
  };

  #draw(now) {
    const ctx = this.#ctx;
    const { base, light, hot, deep } = this.#colors;
    const speedBoost = Math.min(this.#mouse.speed, 3);
    const reach = Math.max(90, this.#w * 0.09) * (1 + speedBoost * 0.35);
    const baseR = this.#gap * 0.2;
    const rippleLife = 1500;
    this.#ripples = this.#ripples.filter((ripple) => now - ripple.t < rippleLife);
    let busy = this.#ripples.length > 0;
    this.#mouse.speed *= 0.92;
    if (this.#mouse.speed > 0.02) busy = true;

    this.#warm += (this.#warmTarget - this.#warm) * 0.08;
    if (Math.abs(this.#warmTarget - this.#warm) > 0.003) busy = true;

    let sweepX = null;
    if (this.#sweep) {
      const age = (now - this.#sweep.t) / 2600;
      if (age >= 1) this.#sweep = null;
      else {
        sweepX = -240 + age * (this.#w + 480);
        busy = true;
      }
    }

    ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    ctx.clearRect(0, 0, this.#w, this.#h);
    const small = this.#w < 640;
    const beat = this.#beat(now);
    if (beat.active) busy = true;
    if (beat.v > 0.01 && this.#beatBox) {
      // Warm glow behind the "4" on each beat.
      const box = this.#beatBox;
      const cx = (box.x0 + box.x1) / 2;
      const cy = (box.y0 + box.y1) / 2;
      const radius = Math.max(box.x1 - box.x0, box.y1 - box.y0) * (0.55 + beat.v * 0.12);
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      glow.addColorStop(0, `rgb(${hot[0]} ${hot[1]} ${hot[2]} / ${(beat.v * 0.22).toFixed(3)})`);
      glow.addColorStop(1, `rgb(${deep[0]} ${deep[1]} ${deep[2]} / 0)`);
      ctx.fillStyle = glow;
      ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
    }
    const mix = (a, b, t) => Math.round(a + (b - a) * t);

    for (const dot of this.#dots) {
      const released = now >= dot.release;
      let targetHeat = this.#warm * (0.35 + dot.seed * 0.25);
      // Morph: switch to the new goal when this dot's turn in the wave comes; it flares coral in flight.
      if (dot.mAt && now >= dot.mAt) {
        dot.gx = dot.nx;
        dot.gy = dot.ny;
        dot.mAt = 0;
        dot.heat = Math.max(dot.heat, 0.55);
      }
      if (dot.mAt) busy = true;
      const hx = dot.x + dot.gx;
      const hy = dot.y + dot.gy;
      let tx = dot.gx;
      let ty = dot.gy;

      if (released && this.#mouse.on) {
        const dx = hx - this.#mouse.x;
        const dy = hy - this.#mouse.y;
        const dist = Math.hypot(dx, dy);
        if (dist < reach) {
          const k = 1 - dist / reach;
          targetHeat = Math.max(targetHeat, k * k);
          const push = k * k * this.#gap * 1.6;
          tx += (dx / (dist || 1)) * push;
          ty += (dy / (dist || 1)) * push;
        }
      }

      for (const ripple of this.#ripples) {
        const age = (now - ripple.t) / rippleLife;
        const radius = age * this.#w * 0.75;
        const band = Math.abs(Math.hypot(hx - ripple.x, hy - ripple.y) - radius);
        if (band < 44) {
          const k = (1 - band / 44) * (1 - age) * ripple.strength;
          targetHeat = Math.max(targetHeat, k);
          ty -= k * this.#gap;
        }
      }

      if (sweepX !== null) {
        const d = (hx - sweepX - (this.#h - hy) * 0.35) / 110;
        targetHeat = Math.max(targetHeat, Math.exp(-d * d) * 0.6);
      }

      // Springs: elastic return with a little overshoot. Embers wait, drifting.
      if (released) {
        dot.vx = (dot.vx + (tx - dot.px) * 0.09) * 0.84;
        dot.vy = (dot.vy + (ty - dot.py) * 0.09) * 0.84;
      } else {
        dot.vx = (dot.vx + Math.sin(now / 400 + dot.seed * 9) * 0.02) * 0.95;
        dot.vy = (dot.vy - 0.01) * 0.95;
      }
      dot.px += dot.vx;
      dot.py += dot.vy;
      dot.heat += (targetHeat - dot.heat) * 0.14;

      if (!released || Math.abs(dot.vx) > 0.02 || Math.abs(dot.vy) > 0.02 || Math.abs(dot.px - tx) > 0.05 || Math.abs(dot.py - ty) > 0.05 || Math.abs(targetHeat - dot.heat) > 0.004) busy = true;

      const h = Math.min(1, dot.heat * 1.5);
      let r;
      let g;
      let b;
      let alpha;
      if (dot.accent && !this.#morphWord) {
        const hb = Math.min(1, h + beat.v * 0.7);
        const warm = hb > 0.5 ? light : hot;
        r = mix(hot[0], warm[0], hb);
        g = mix(hot[1], warm[1], hb);
        b = mix(hot[2], warm[2], hb);
        alpha = 0.55 + dot.seed * 0.15 + dot.heat * 0.3 + beat.v * 0.3;
      } else {
        const warm = dot.heat > 0.6 ? deep : hot;
        r = mix(base[0], warm[0], h);
        g = mix(base[1], warm[1], h);
        b = mix(base[2], warm[2], h);
        alpha = (small ? 0.34 : 0.22) + dot.seed * 0.1 + dot.heat * 0.7;
      }
      if (!released) alpha *= 0.25 + 0.2 * Math.sin(now / 160 + dot.seed * 20);
      ctx.fillStyle = `rgb(${r} ${g} ${b} / ${Math.max(0, alpha).toFixed(3)})`;
      ctx.beginPath();
      const pulse = dot.accent && !this.#morphWord ? beat.v * this.#gap * 0.24 : 0;
      ctx.arc(dot.x + dot.px, dot.y + dot.py, baseR + dot.heat * this.#gap * 0.42 + pulse, 0, Math.PI * 2);
      ctx.fill();
    }
    if (!busy) this.#scheduleBeat(now);
    return busy;
  }

  /** Heartbeat of the "4": a lub-dub every 2.4s, only after the word has assembled. */
  #beat(now) {
    if (this.#still || !this.#beatBox || this.#morphWord || now < this.#beatFrom) return { v: 0, active: false };
    const phase = (now - this.#beatFrom) % 2400;
    const pulse = (center, width, height) => height * Math.exp(-(((phase - center) / width) ** 2));
    return { v: pulse(110, 70, 1) + pulse(360, 90, 0.65), active: phase < 650 };
  }

  #scheduleBeat(now) {
    clearTimeout(this.#beatTimer);
    if (this.#still || !this.#visible || !this.#beatBox || this.#beatFrom === Infinity) return;
    const wait = now < this.#beatFrom ? this.#beatFrom - now : 2400 - ((now - this.#beatFrom) % 2400);
    this.#beatTimer = setTimeout(() => this.#wake(), Math.max(16, wait));
  }
}

if (!customElements.get('s-dot-word')) customElements.define('s-dot-word', SDotWord);
